/**
 * §M2 · Enlace contextual PANTALLA → GUÍA, montado UNA vez en el shell.
 *
 * Antes cada pantalla decidía si ponía su «?» — y diecisiete lo ponían y el
 * resto no. Aquí el enlace se DERIVA de la ruta activa (`/academy/cohorts` →
 * área `academy.cohorts` ▸ `academy`), así que aparece en todas por
 * construcción y no hay una lista que mantener.
 *
 * Filtrado por ROL y por FLAG, que es el requisito real: nunca se ofrece la
 * documentación de algo que el usuario no puede usar. Si la ruta corresponde a
 * un item del nav que su rol no ve, o a una feature que su academia apagó, el
 * enlace no se pinta — leer cómo usar una pantalla a la que no puedes entrar
 * solo genera frustración.
 */
import { createMemo, Show } from "solid-js";
import { useLocation } from "@tanstack/solid-router";

import { stripUPrefix } from "@/lib/active-account";
import { NAV_SECTIONS } from "@/features/navigation/nav-model";
import { useNavContext } from "@/features/navigation/use-nav-context";
import { helpAreaForPath } from "@/features/profiles/profile-model";
import { HelpButton } from "./help-button";
import { helpTargetForArea } from "../registry";

/** Área de ayuda con la que se etiqueta el botón (para el testid estable). */
function areaKeyForPath(path: string): string | undefined {
  const segments = path.split("/").filter((s) => s.length > 0);
  for (let depth = Math.min(segments.length, 2); depth >= 1; depth -= 1) {
    const key = segments.slice(0, depth).join(".");
    if (helpTargetForArea(key)) {
      return key;
    }
  }
  return undefined;
}

/**
 * ¿Puede este usuario USAR la pantalla que cubre `path`? Se responde con el
 * mismo nav-model que gobierna la barra lateral: si ningún item de nav de esa
 * área es visible para su contexto, la pantalla no es suya.
 *
 * Las rutas sin item de nav propio (detalles como `/learn/$courseId`, o
 * `/notifications`) no se esconden: el usuario ya está dentro.
 */
function usableByContext(path: string, ctx: ReturnType<typeof useNavContext>): boolean {
  const root = `/${path.split("/").filter((s) => s.length > 0)[0] ?? ""}`;
  const items = NAV_SECTIONS.flatMap((s) => s.items).filter(
    (item) => item.to === path || item.to.startsWith(`${root}/`) || item.to === root,
  );
  if (items.length === 0) {
    return true;
  }
  const context = ctx();
  return items.some(
    (item) =>
      item.visible(context) && (item.feature === undefined || context.featureEnabled(item.feature)),
  );
}

export function RouteHelpLink() {
  const location = useLocation();
  const navContext = useNavContext();
  // La ruta real vive bajo `/u/<n>`; el nav-model habla en rutas sin ese prefijo.
  const path = createMemo(() => stripUPrefix(location().pathname));
  const area = createMemo(() => {
    const p = path();
    return helpAreaForPath(p) && usableByContext(p, navContext) ? areaKeyForPath(p) : undefined;
  });

  return (
    <Show when={area()}>
      {(resolved) => (
        <div class="pointer-events-none sticky top-0 z-20 flex h-0 justify-end px-3 pt-3 md:px-5">
          <div class="pointer-events-auto rounded-full bg-background/70 backdrop-blur-sm">
            <HelpButton area={resolved()} />
          </div>
        </div>
      )}
    </Show>
  );
}
